import * as _ from "lodash";
import moment from "moment";

import { memoize, Nullable } from "@ctra/utils";
import { Table as BaseTable } from "@ctra/charts";
import { DataDescriptorEntity, Enterprise as App, TableMeta } from "@ctra/api";
import { Enterprise, i18nTranslate } from "@ctra/i18n";

import { XAxisType } from "./xAxis";
import { ChartViewBase } from "./Base";

const {
  chart: { series }
} = Enterprise;

/**
 * Table view class
 */
class Table extends ChartViewBase<TableMeta> {
  /**
   * Get the chart component
   * @return {ReturnType<typeof ChartViewBase.prototype.getComponent>}
   */
  @memoize
  getComponent(): ReturnType<typeof ChartViewBase.prototype.getComponent> {
    return BaseTable;
  }

  /**
   * Get the metric variant
   * @returns {Nullable<DataDescriptorEntity>}
   */
  @memoize
  getMetricVariant(): Nullable<DataDescriptorEntity> {
    const { variantId } = this.metadata;
    const dataDictionary = App.entities.getDataDictionary(App.store.getState());

    return _.get(dataDictionary, variantId, null);
  }

  /**
   * Get the metadata
   * @return {ReturnType<typeof ChartViewBase.prototype.getMetadata>}
   */
  @memoize
  getMetadata(): ReturnType<typeof ChartViewBase.prototype.getMetadata> {
    const variant = this.getMetricVariant();
    const typeName = variant?.dataProperties.typeName;

    return {
      /**
       * Tables do not use the same set of metas as line, bar etc
       * Hence no need extending ...super.getMetadata()
       */
      ...this.metadata,
      columns: _.map(this.metadata.columns, (column, idx) => ({
        ...column,
        /**
         * Translate legends only if they are translatable
         */
        title:
          !idx || (this.entity.flags.isSeriesTranslatable && !_.isFinite(+(column.title as string)))
            ? i18nTranslate(series(column.title as string), { typeName })
            : column.title
      })),
      keyRows: this.getTranslatedKeyRowLabels(this.metadata.keyRows, { long: false })
    };
  }

  /**
   * Translate keyRows based on the type
   * @param {Record<string, string>} keyRows
   * @param {{long: boolean}} options
   * @return {Record<string, string>}
   * @private
   */
  private getTranslatedKeyRowLabels(
    keyRows: Record<string, string>,
    options: { long: boolean }
  ): Record<string, string> {
    const xAxisType = XAxisType.create(this.metadata.xType);
    const list = _.keys(keyRows);

    /**
     * Check if all the dates are in the same year
     * @type {boolean}
     */
    const isSameYear: boolean =
      list.length <= 1 || _.every(list, (date) => moment(date).year() === moment(list[0]).year());

    return _.reduce<string, Record<string, string>>(
      list,
      (acc, date, index, collection) => {
        acc[date] = xAxisType.getTranslatedLabel(date, { long: options.long, appendYear: !isSameYear });

        return acc;
      },
      {}
    );
  }
}

export { Table };
